import React, { useState } from 'react';
import { register } from '../services/UserService';
import { useAuth } from '../context/AuthContext';
import './Forms.css';
import { getUserFacingErrorMessage } from '../services/errors';
import {
  PASSWORD_REQUIREMENTS_TEXT,
  validateEmail,
  validatePasswordStrength,
} from '../utils/validation';

const SignUpForm = ({ onSignUp }) => {
  const [form, setForm] = useState({ email: '', name: '', password: '' });
  const [serverError, setServerError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');
    setEmailError('');
    setPasswordError('');
    try {
      const emailCheck = validateEmail(form.email);
      if (!emailCheck.ok) {
        setEmailError(emailCheck.message);
        return;
      }

      const pwCheck = validatePasswordStrength(form.password);
      if (!pwCheck.ok) {
        setPasswordError(pwCheck.message);
        return;
      }

      const data = await register({
        ...form,
        email: emailCheck.normalized,
      });
      // If backend returns token on register, log in immediately to populate context
      if (data?.token) {
        login(data.token);
      }
      if (onSignUp) onSignUp(data);
    } catch (err) {
      // Only show server/API errors at the top of the form.
      // Field-level validation errors are handled next to their inputs.
      const msg = getUserFacingErrorMessage(err, 'register');
      setServerError(msg || 'Registration failed. Please try again.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className='form'>
      <h3 className='form-title'>Register</h3>
      <label className='label'>Email</label>
      <input
        className='input'
        type='email'
        name='email'
        value={form.email}
        onChange={handleChange}
        onBlur={() => {
          if (!form.email) return;
          const emailCheck = validateEmail(form.email);
          if (!emailCheck.ok) {
            setEmailError(emailCheck.message);
          } else {
            setEmailError('');
          }
        }}
        required
      />
      {emailError && <p className='form-error'>{emailError}</p>}
      <p className='form-disclaimer'>
        We respect your privacy. We will only use your email for account
        communication related to this service. We will not sell or share your
        email with third parties, and we will not send marketing emails without
        your explicit consent.
      </p>
      <label className='label'>Name</label>
      <input
        className='input'
        type='text'
        name='name'
        value={form.name}
        onChange={handleChange}
        required
      />
      <label className='label'>Password</label>
      <div style={{ position: 'relative' }}>
        <input
          className='input'
          type={showPassword ? 'text' : 'password'}
          name='password'
          value={form.password}
          onChange={(e) => {
            handleChange(e);
            if (passwordError) setPasswordError('');
          }}
          onBlur={() => {
            if (!form.password) return;
            const pwCheck = validatePasswordStrength(form.password);
            if (!pwCheck.ok) setPasswordError(pwCheck.message);
          }}
          style={{ paddingRight: 44 }}
          required
        />
        <button
          type='button'
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          onClick={() => setShowPassword((v) => !v)}
          style={{
            position: 'absolute',
            right: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            padding: 6,
            lineHeight: 1,
            color: 'inherit',
            opacity: 0.85,
          }}
        >
          {showPassword ? 'Hide' : 'Show'}
        </button>
      </div>
      {passwordError ? (
        <p className='form-error'>{passwordError}</p>
      ) : (
        <p className='form-disclaimer'>{PASSWORD_REQUIREMENTS_TEXT}</p>
      )}
      <div>
      {serverError && <p className='form-error'>{serverError}</p>}  
      </div>
      <div className='form-actions'>
        <button className='btn btn--primary' type='submit'>
          Sign Up
        </button>
      </div>
      
    </form>
    
  );
};

export default SignUpForm;
